'use client';

import React from 'react';
import { canonicalLocalizedPath, localizeCalculatorPath } from '@/lib/localized-paths';
import { splitLocale, toExternal, toInternal } from '@/lib/locale-path';
// Prefetched on intent, not on sight (components/IntentLink.tsx).
import Link from '@/components/IntentLink';
import { usePathname, useSearchParams } from 'next/navigation';
import { markLanguageSwitch } from '@/lib/wizard-draft';

interface LanguageSwitcherProps {
  lang: string;
  className?: string;
  customEnPath?: string;
  customFrPath?: string;
}

export default function LanguageSwitcher({
  lang,
  className = '',
  customEnPath,
  customFrPath
}: LanguageSwitcherProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString();
  const suffix = search ? `?${search}` : '';

  let enPath = customEnPath;
  let frPath = customFrPath;

  if (!enPath || !frPath) {
    // The English address has no prefix and the French one starts with /fr (lib/locale-path.ts): the same page is the rest of the path.
    const { rest } = splitLocale(pathname || '/');
    enPath = rest;
    frPath = rest === '/' ? '/fr' : `/fr${rest}`;
    // Each language's own words for the page (/en/listings, /fr/annonces):
    // every localized route, not two of them. A link to the other language's
    // word was redirected by proxy.ts, and Next 16 prefetches a link that
    // redirects again and again - 300 requests in 8 s on /en/listings.
    // (the names are filed under /en and /fr, so the English one is looked at as /en/... and shown without it)
    enPath = toExternal(localizeCalculatorPath(canonicalLocalizedPath(toInternal(enPath)) ?? toInternal(enPath)));
    frPath = localizeCalculatorPath(canonicalLocalizedPath(frPath) ?? frPath);
    // The visitor's search, sort and filter choices carry over between languages.
    enPath = `${enPath}${suffix}`;
    frPath = `${frPath}${suffix}`;
  }

  const isFr = lang === 'fr';

  return (
    <div className={`inline-flex items-center bg-neutral-800 p-0.5 rounded-full border border-neutral-700/80 shadow-sm ${className}`}>
      <Link
        href={enPath || '/'}
        onClick={markLanguageSwitch}
        className={`px-2.5 py-1.5 rounded-full text-xs font-extrabold tracking-wide transition cursor-pointer select-none ${
          !isFr
            ? 'bg-gold text-black shadow-sm'
            : 'text-neutral-400 hover:text-white'
        }`}
      >
        EN
      </Link>
      <Link
        href={frPath || '/fr'}
        onClick={markLanguageSwitch}
        className={`px-2.5 py-1.5 rounded-full text-xs font-extrabold tracking-wide transition cursor-pointer select-none ${
          isFr
            ? 'bg-gold text-black shadow-sm'
            : 'text-neutral-400 hover:text-white'
        }`}
      >
        FR
      </Link>
    </div>
  );
}
